/**
 * Gebruikers en rechten (29-09-2026): wie heeft ingelogd, en wat mag diegene.
 *
 * Entra ID beslist wie er binnen mag; een beheerder kan hier de rol bijstellen
 * (die vervangt dan de rol uit Entra) en de naam die het platform toont. De
 * backend beslist per verzoek opnieuw; wat hier staat bepaalt alleen wat het
 * scherm laat zien.
 */
import { api } from "./client";

export type PlatformRole = "viewer" | "editor" | "admin";

/** De ingelogde gebruiker zoals het platform hem ziet. */
export type Me = {
  subject: string;
  name: string | null;
  email: string | null;
  roles: string[];
  token_roles: string[];
  /** `platform`: een beheerder heeft de rol gezet; `entra`: de rol komt uit het token. */
  role_source: "platform" | "entra";
  /** Sturingsrollen (K10): welke dashboardlagen deze gebruiker ziet, in vaste volgorde. */
  steering_layers: SteeringLayerCode[];
};

/** De drie sturingsrollen (K10, migratie 0075). */
export type SteeringLayerCode = "strategic" | "tactical" | "operational";

export type PlatformUser = {
  id: string;
  subject: string;
  email: string | null;
  /** De naam uit het laatste token (Microsoft). */
  token_name: string | null;
  /** De naam die een beheerder heeft gezet; `null` = de naam uit Microsoft. */
  display_name: string | null;
  /** De naam die het platform toont. */
  name: string;
  token_roles: string[];
  /** De rol die een beheerder heeft gezet; `null` = volgt Entra ID. */
  role: PlatformRole | null;
  /** De rollen die gelden. */
  roles: string[];
  changed_by: string | null;
  changed_at: string | null;
  first_seen_at: string;
  last_seen_at: string;
  is_self: boolean;
  /**
   * Sturingsrollen (K10, migratie 0075): wat een beheerder heeft gezet, en wat
   * geldt. Een beheerder (`admin`) ziet altijd alle drie (`steering_layers_fixed`).
   */
  steering_layers: SteeringLayerCode[];
  effective_steering_layers: SteeringLayerCode[];
  steering_layers_fixed: boolean;
  /** De laag waarop het dashboard opent, of `null` zonder sturingsrol. */
  opens_on: SteeringLayerCode | null;
};

/**
 * Een uitnodiging vanuit het platform (migratie 0038). `pending`: nog niet
 * ingelogd · `active`: heeft ingelogd · `revoked`: toegang ingetrokken.
 */
export type PlatformInvitation = {
  id: string;
  email: string;
  role: PlatformRole;
  steering_layers: SteeringLayerCode[];
  status: "pending" | "active" | "revoked";
  invited_by_name: string;
  invited_at: string;
  last_sent_at: string;
  send_count: number;
  revoked_at: string | null;
  account_id: string | null;
  account_name: string | null;
  last_seen_at: string | null;
};

export type PlatformUserList = {
  users: PlatformUser[];
  roles: PlatformRole[];
  steering_layers: SteeringLayerCode[];
  invitations: PlatformInvitation[];
  /** Kan deze omgeving uitnodigingen versturen? Lokaal niet. */
  may_invite: boolean;
};

export type InvitationRequest = {
  email: string;
  role: PlatformRole;
  name?: string;
  steering_layers?: SteeringLayerCode[];
};

export type PlatformUserUpdate = {
  role?: PlatformRole | null;
  display_name?: string | null;
  steering_layers?: SteeringLayerCode[];
};

export const fetchMe = async (): Promise<Me> => (await api.get<Me>("/me")).data;

export const fetchUsers = async (): Promise<PlatformUserList> =>
  (await api.get<PlatformUserList>("/users")).data;

export const updateUser = async (id: string, update: PlatformUserUpdate): Promise<PlatformUser> =>
  (await api.patch<PlatformUser>(`/users/${id}`, update)).data;

/** Nodigt iemand uit, of stuurt de uitnodiging opnieuw. Microsoft verstuurt de mail. */
export const inviteUser = async (request: InvitationRequest): Promise<PlatformInvitation> =>
  (await api.post<PlatformInvitation>("/users/invitations", request)).data;

/** Trekt de toegang in die met een uitnodiging is gegeven. */
export const revokeInvitation = async (id: string): Promise<PlatformInvitation> =>
  (await api.delete<PlatformInvitation>(`/users/invitations/${id}`)).data;
